import React, { useState } from "react";
import { Link as RouterLink } from "react-router-dom";
import {
  Typography,
  Box,
  Paper,
  TextField,
  Button,
  Link,
  CircularProgress,
  Alert,
} from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import type { TFunction } from "i18next";
import { useTranslation } from "react-i18next";
import { AuthService } from "../../services/auth/auth.service";
import { handleApiError } from "../../utils/error-handler";

/**
 * Схема восстановления пароля — ФАБРИКА, а не константа модуля (AD-44).
 *
 * Сообщение схемы — обычная строка, вычисленная в момент её сборки. Константа
 * уровня модуля собиралась бы один раз при первом импорте и навсегда фиксировала
 * бы действовавший тогда язык: до правки здесь стояли русские литералы, и на
 * немецком экране под полем «E-Mail» печаталось «Введите корректный email».
 * Схема собирается на каждом рендере; `useForm` переписывает `control._options`
 * при каждом вызове (react-hook-form 7.33.1), поэтому валидация берёт свежую.
 */
const createForgotPasswordSchema = (t: TFunction<"common">) =>
  yup.object({
    email: yup
      .string()
      .email(t("auth.validation.emailInvalid"))
      .required(t("auth.validation.emailRequired")),
  });

type ForgotPasswordFormData = yup.InferType<
  ReturnType<typeof createForgotPasswordSchema>
>;

const ForgotPasswordPage: React.FC = () => {
  const { t } = useTranslation("common");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ForgotPasswordFormData>({
    resolver: yupResolver(createForgotPasswordSchema(t)),
    defaultValues: {
      email: "",
    },
  });

  const onSubmit = async (data: ForgotPasswordFormData) => {
    try {
      setLoading(true);
      setError(null);
      await AuthService.forgotPassword(data.email);
      setSuccess(true);
    } catch (err: unknown) {
      setError(handleApiError(err, t("auth.forgotPasswordPage.requestError")));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        p: 2,
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "calc(100vh - 64px)",
      }}
    >
      <Paper
        sx={{
          maxWidth: 480,
          width: "100%",
          p: 4,
          boxShadow: (theme) => theme.shadows[3],
        }}
      >
        <Typography variant="h4" align="center" gutterBottom>
          {t("auth.forgotPasswordPage.title")}
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {success ? (
          <>
            <Alert severity="success" sx={{ mb: 3 }}>
              {t("auth.forgotPasswordPage.sent")}
            </Alert>
            <Box sx={{ textAlign: "center", mt: 2 }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {t("auth.forgotPasswordPage.backToLogin")}
              </Link>
            </Box>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Typography variant="body2" sx={{ mb: 3 }}>
              {t("auth.forgotPasswordPage.intro")}
            </Typography>

            <Box sx={{ mb: 2 }}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label={t("auth.email")}
                    fullWidth
                    variant="outlined"
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={loading}
                  />
                )}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <Button
                variant="contained"
                color="primary"
                fullWidth
                type="submit"
                disabled={loading}
              >
                {loading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : (
                  t("auth.forgotPasswordPage.submit")
                )}
              </Button>
            </Box>

            <Box sx={{ textAlign: "center" }}>
              <Link component={RouterLink} to="/login" variant="body2">
                {t("auth.forgotPasswordPage.backToLogin")}
              </Link>
            </Box>
          </form>
        )}
      </Paper>
    </Box>
  );
};

export default ForgotPasswordPage;
